'use client';

import { useState, useEffect, useCallback } from 'react';
import { TravelConfig, LegacyLocation, TimeData } from '@/types';
import { parseTravelConfig, ValidationError } from '@/utils/validation';

export const useTravelConfig = () => {
  const [travelConfig, setTravelConfig] = useState<TravelConfig | null>(null);
  const [currentLocation, setCurrentLocation] = useState<LegacyLocation | null>(null);
  const [timeData, setTimeData] = useState<TimeData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper method to get UTC offset from timezone
  const getUtcOffset = useCallback((timezone: string): string => {
    try {
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: timezone,
        timeZoneName: 'shortOffset'
      });

      const parts = formatter.formatToParts(new Date());
      const offsetPart = parts.find(part => part.type === 'timeZoneName');

      return offsetPart ? offsetPart.value : 'UTC';
    } catch (error) {
      // Type-safe error handling with specific error context
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.warn(`Invalid timezone "${timezone}": ${errorMessage}. Falling back to UTC.`);

      // Set error state for better UX
      setError(`Invalid timezone configuration: ${timezone}`);
      return 'UTC';
    }
  }, []);

  // Update time data for current location
  const updateTimeData = useCallback((location: LegacyLocation) => {
    if (!location) return;

    try {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: location.timezone
      });

      const dateString = now.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'short',
        timeZone: location.timezone
      });

      const utcOffset = getUtcOffset(location.timezone);

      setTimeData({
        timeString,
        dateString,
        utcOffset,
        location
      });
    } catch (error) {
      // Type-safe error handling with specific context
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorContext = `Failed to update time data for ${location.city}, ${location.country} (${location.timezone})`;
      console.error(`${errorContext}: ${errorMessage}`);

      // Set a more specific error state
      setError(`Time update failed for ${location.city}: ${errorMessage}`);
    }
  }, [getUtcOffset]);

  // Update current location based on travel schedule
  const updateCurrentLocation = useCallback((config: TravelConfig, shouldUpdateTime = true) => {
    if (!config) return;

    const now = new Date();
    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD format

    // Check if currently traveling
    const currentTravel = config.travels.find(travel => {
      return currentDate >= travel.startDate && currentDate <= travel.endDate;
    });

    let location: LegacyLocation;
    if (currentTravel) {
      location = { ...currentTravel.location, isTravel: true };
    } else {
      location = { ...config.homeLocation, isTravel: false };
    }

    setCurrentLocation(location);

    // Conditionally update time data to prevent race condition
    if (shouldUpdateTime) {
      updateTimeData(location);
    }

    return location;
  }, [updateTimeData]);

  // Remove the separate effect that responds to location change
  // This was causing the circular dependency

  // Load travel configuration
  useEffect(() => {
    const loadTravelConfig = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/travel-config.json');

        if (!response.ok) {
          throw new Error('Failed to load travel configuration');
        }

        const responseText = await response.text();
        const config = parseTravelConfig(responseText);
        setTravelConfig(config);
        updateCurrentLocation(config);
      } catch (err) {
        const errorMessage = err instanceof ValidationError
          ? `Configuration validation failed: ${err.message}`
          : err instanceof Error
            ? `Failed to load travel configuration: ${err.message}`
            : 'Unknown error occurred while loading configuration';

        console.warn(errorMessage, err);
        // Fallback to Bangkok if config fails to load
        const fallbackConfig: TravelConfig = {
          homeLocation: {
            city: "Bangkok",
            country: "Thailand",
            timezone: "Asia/Bangkok"
          },
          travels: []
        };
        setTravelConfig(fallbackConfig);
        updateCurrentLocation(fallbackConfig);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    loadTravelConfig();
  }, [updateCurrentLocation]);

  // Real-time clock updates
  useEffect(() => {
    if (!currentLocation) return;

    let lastLocationCheck = 0;

    const updateTime = () => {
      updateTimeData(currentLocation);

      // Check for location changes once per minute using timestamp approach
      const now = Date.now();
      if (now - lastLocationCheck >= 60000 && travelConfig) { // 60 seconds
        lastLocationCheck = now;
        updateCurrentLocation(travelConfig, false);
      }
    };

    // Update every second
    const intervalId = setInterval(updateTime, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [currentLocation, travelConfig, updateTimeData, updateCurrentLocation]);

  return {
    travelConfig,
    currentLocation,
    timeData,
    isLoading,
    error,
    refreshLocation: () => travelConfig && updateCurrentLocation(travelConfig)
  };
};
